'use client';

import React, { FC, useRef, useState } from 'react';
import Slider, { Settings } from 'react-slick';
import {
  TypeCarouselFields,
  TypeCarouselSlide,
  TypeImage,
} from '@/core/generated-types';
import { ContentEntry } from '@/core/contentfulConfig';
import { Arrow } from './Arrows';
import { formatImageObject } from '@/utils/helpers';
import { LuvImage } from '@/components';
import { BREAKPOINTS, CAROUSEL_SLIDES_TO_SHOW } from '@/core/constants';
import { UniversalCta } from '../UniversalCta';

const CarouselWithTitle: FC<TypeCarouselFields> = ({
  carouselTitle,
  carouselDescription,
  id,
  components = [],
  cta,
  backgroundColor,
  shouldSlide = true,
}) => {
  const sliderRef = useRef<Slider>(null);
  const [slideDescription, setSlideDescription] = useState('');
  const sliderImages = components?.map(
    (component: ContentEntry<TypeCarouselSlide>) => {
      const { fields } = component;
      const imageUrl = fields?.component?.fields?.Image?.fields?.file?.url;
      const altText = fields?.component?.fields?.altText || 'lovevery';
      return formatImageObject(altText, imageUrl);
    },
  );
  const maxSlidesToShow = components.length - 1;
  const settings: Settings = {
    slidesToShow: Math.min(maxSlidesToShow, CAROUSEL_SLIDES_TO_SHOW),
    arrows: shouldSlide ? true : false,
    centerMode: true,
    infinite: true,
    nextArrow: <Arrow direction={'right'} />,
    prevArrow: <Arrow direction={'left'} />,
    afterChange: (current: number) => {
      const slideTitle = components?.[current]?.fields?.slideTitle;
      slideTitle && setSlideDescription(slideTitle as string);
    },
    onInit: () => {
      const slideTitle = components?.[0]?.fields?.slideTitle;
      setSlideDescription(slideTitle as string);
    },
    responsive: [
      {
        breakpoint: BREAKPOINTS.lg,
        settings: {},
      },
      {
        breakpoint: BREAKPOINTS.md,
        settings: {
          arrows: false,
          slidesToShow: Math.min(maxSlidesToShow, 3),
        },
      },
      {
        breakpoint: BREAKPOINTS.sm,
        settings: {
          arrows: false,
          slidesToShow: Math.min(maxSlidesToShow, 3),
        },
      },
    ],
  };

  return (
    <div
      data-testid={id}
      className={`w-full py-10 text-center lg:py-20 bg-${backgroundColor?.fields?.color}`}
    >
      <div className="mx-auto flex max-w-[1300px] flex-col content-center md:px-[44px]">
        <div className="container mx-auto px-4">
          {carouselTitle && (
            <h2
              className="title-container mb-4 text-center text-h2 font-bold md:text-h1 lg:mb-8"
              data-testid="carousel-header"
            >
              {carouselTitle}
            </h2>
          )}
          {carouselDescription && (
            <p className="text-center">{carouselDescription}</p>
          )}
          <p
            className="mb-8 text-center"
            data-testid="carousel-slideDescription"
          >
            {slideDescription}
          </p>
        </div>
        <div className={`slider-variant-${id}`}>
          <Slider ref={sliderRef} {...settings}>
            {sliderImages.map((imageData: TypeImage, i: React.Key) => {
              return (
                <div className="p-2 lg:p-12" key={i}>
                  <div className="flex h-full w-full items-center justify-center">
                    <LuvImage
                      alt={imageData.fields?.altText}
                      className="mx-auto"
                      height={100}
                      imageData={imageData}
                      width={100}
                    />
                  </div>
                </div>
              );
            })}
          </Slider>
        </div>
        {cta && (
          <UniversalCta
            link={cta}
            className="self-center pt-12 text-center"
            defaultButtonStyle="Link"
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(CarouselWithTitle);
